/**
 * Invite dialog brand selection (collapsible checklist).
 * Shared by the Specialist ("user") and the Manager "Selected brands only" invite paths.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Checkbox } from '@/components/ui/Checkbox';

interface BrandOption {
  id: number;
  name: string;
}

interface InviteBrandAccessSectionProps {
  brands: BrandOption[] | undefined;
  isLoading: boolean;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  /** true = at least one brand must be selected (manager limited scope), false = optional (specialist) */
  required?: boolean;
  /** start expanded (a required selection should not be hidden) */
  defaultExpanded?: boolean;
}

export const InviteBrandAccessSection = ({
  brands,
  isLoading,
  selectedIds,
  onChange,
  required = false,
  defaultExpanded = false,
}: InviteBrandAccessSectionProps) => {
  const { t } = useTranslation('agency');
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggle = (id: number): void => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon name={ICONS.LINK} size="sm" color="text-gray-600" />
          <span className="text-sm font-medium text-gray-700">
            {t('agency_team_client_access_title')}
            {selectedIds.length > 0 && !expanded && (
              <span className="ml-2 text-primary-600">({selectedIds.length} {t('agency_team_client_access_selected')})</span>
            )}
          </span>
          <span className="text-xs text-gray-500">
            ({required ? t('agency_team_client_access_required') : t('agency_team_client_access_optional')})
          </span>
        </div>
        <Icon name={expanded ? ICONS.ARROW_UP : ICONS.ARROW_DOWN} size="sm" color="text-gray-400" />
      </button>

      {expanded && (
        <div className="p-4 border-t border-gray-200 space-y-2 max-h-48 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Icon name={ICONS.LOADER} size="sm" className="animate-spin" color="text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">{t('agency_team_loading_clients')}</span>
            </div>
          ) : brands && brands.length > 0 ? (
            brands.map((client) => (
              <label key={client.id} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                <Checkbox
                  checked={selectedIds.includes(client.id)}
                  onCheckedChange={() => toggle(client.id)}
                  aria-label={client.name}
                />
                <span className="text-sm text-gray-700">{client.name}</span>
              </label>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center py-2">{t('agency_team_no_clients')}</p>
          )}
        </div>
      )}
    </div>
  );
};
